import React, { useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/redux';

import { Box, Link, Typography } from '@mui/material';
import {
  BrandLogo,
  PositionSubscriptionForm,
  TextButton,
} from '../../components';
import { SuccessfulSubscription } from './components/SuccessfulSubscription';

import {
  createReferralPositionSubscription,
  positionSubscriptionSelectors,
} from 'src/redux/positionSubscription';
import { referFriendSelectors } from 'src/redux/referFriend';

import { PositionSubscriptionFields } from 'src/types';
import { PositionSubscriptionType, ReferrerType } from 'src/constants';
import { isStatusComplete } from 'src/utils';

import { createReferralPositionSubscriptionValidationSchema } from './validationsSchema';

export const PositionSubscriptionReferral: React.FC = () => {
  const dispatch = useAppDispatch();

  const { data: referrerProfile } = useAppSelector(
    referFriendSelectors.getReferrerAPIData,
  );
  const createPositionSubscriptionAPIStatus = useAppSelector(
    positionSubscriptionSelectors.getCreateSubscriptionAPIStatus,
  );

  const referralEmail = referrerProfile?.email || null;

  const initialValues: PositionSubscriptionFields = useMemo(
    () => ({
      email: referralEmail,
    }),
    [referralEmail],
  );
  const disabledFields: Array<keyof PositionSubscriptionFields> = useMemo(
    () => ['email'],
    [],
  );

  const requiredFields: Array<keyof PositionSubscriptionFields> = useMemo(
    () => ['email', 'frequency'],
    [],
  );

  const onPositionSubscriptionFormSubmit = useCallback(
    (values: PositionSubscriptionFields) => {
      dispatch(
        createReferralPositionSubscription({
          locations: values.locations || null,
          technicalFlows: values.technicalFlows || null,
          frequency: values.frequency!,
        }),
      );
    },
    [],
  );

  if (referrerProfile?.type !== ReferrerType.INTERNAL) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          padding: 4,
        }}
      >
        <Typography>You are logged in as {referrerProfile?.email}</Typography>
        <TextButton onClick={() => window.history.back()}>Back</TextButton>
      </Box>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        background: theme.palette.background.backgroundPrimary,
        minWidth: '100%',
        minHeight: '100%',
        padding: '41px 16px',
        [theme.breakpoints.down('lg')]: {
          paddingTop: '130px',
        },
      })}
    >
      <Box sx={{ position: 'absolute', top: '49px', left: '66px' }}>
        <BrandLogo width={133} />
      </Box>
      <Box display="flex" alignItems="center" flexDirection="column">
        <Box
          sx={(theme) => ({
            background: theme.palette.common.white,
            borderRadius: '48px',
            width: { xs: '100%', md: '603px' },
            maxWidth: '603px',
            position: 'relative',
          })}
        >
          {!isStatusComplete(createPositionSubscriptionAPIStatus) ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                padding: { xs: '62px 32px', md: '62px 100px' },
                width: '100%',
              }}
            >
              <PositionSubscriptionForm
                title={
                  <>
                    Get notified about our
                    <br />
                    newest positions
                  </>
                }
                initialValues={initialValues}
                disabledFields={disabledFields}
                requiredFields={requiredFields}
                validationSchema={
                  createReferralPositionSubscriptionValidationSchema
                }
                type={PositionSubscriptionType.Referral}
                submissionBtnLabel="Subscribe"
                apiStatus={createPositionSubscriptionAPIStatus}
                onSubmit={onPositionSubscriptionFormSubmit}
              />
            </Box>
          ) : (
            <SuccessfulSubscription />
          )}
        </Box>
        <Box display="flex" justifyContent="center" mt={6}>
          <Typography
            variant="body2"
            color="secondary"
            textAlign="center"
            fontSize="0.675rem"
            mx={2}
          >
            <Link component={RouterLink} to="/terms-of-use">
              Terms of use
            </Link>
          </Typography>
          <Typography
            variant="body2"
            color="secondary"
            textAlign="center"
            fontSize="0.675rem"
            mx={2}
          >
            <Link href="https://agileengine.com/privacy-policy" target="_blank">
              Privacy policy
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
